import {
  Holder,
  Shareholder,
  IHolderCommon,
  IHolderBankAccount,
} from './FormAccount'
import { FormFiles } from './FormFiles'
import { AccountType, IFormRedeemInventory, ITradeFormRedeem, UG_JOINT_HOLDER_NAME } from './types'
import { FundID } from '@/api/funds/enum'
import { DealingMultiForm, IFormMultiRedeem, IFormMultiSubscribe, IFormMultiSwitch, IRedeemSet, ISubscribeSet, ISwitchSet } from './v3/types';
import { toValue } from 'vue'
import deepAssign from '@/utils/deepAssign'

/** 可填寫資料，預設值為 null */
export type Fillable<T> = {
  [K in keyof T]: T[K] extends object
  // 如果是 object 就跑遞迴
  ? Fillable<T[K]>
  // 其餘的類型通通允許 null
  : NonNullable<T[K]> | null
} | T;

/** 彈性資料結構，可為填寫的或是原始固定值 */
export type Flexible<T> = Fillable<T> | T

/** 交易表單 */
export type DealingForm = IFormSubscription | IFormRedeem | IFormSwitch

/** 交易帳戶 */
export interface ITradeAccount {
  /** 既有帳戶號碼 */
  account_id?: number
  /** 帳戶類型 */
  account_type: AccountType
  /** 帳戶持有人 */
  holder: Holder | null
  /** 共同持有人 */
  shareholders?: Shareholder[]
  /** 銀行帳戶資訊 v3.0 從 Holder 拆分到此 */
  bank: IHolderBankAccount

  // 以下由 API 提供

  /** 初次進行線上交易 */
  initial?: boolean
  /** 正式帳戶資料最後寫入時間 */
  formal_submitted_at?: string | null
  /** 保銀留存資料 */
  initial_data?: { field: string, value: string | null, ord?: number | null, rpt_date?: string | null, upd_datetime?: string | null }[]
  /** UG holder_pid(系統辨識用-唯一值) */
  holder_pid?: number
  /** 登入帳號 */
  usercode?: string
  /** HSBC Client ID(一定會有值) */
  hsbc_client_id?: string
  /** HSBC Holder ID (不一定有值) */
  hsbc_holder_id?: string
  /** BNP Holder ID (不一定有值) */
  bnp_holder_id?: string
}
export interface IFormBasic {
  /** 此交易的 Agent (如果為 Dealing 填單) */
  agent_id?: number
  /** Sub-sales (如果為 Dealing 填單, depends on agent) */
  sub_sales_id?: number
  /** 交易帳戶 */
  account: ITradeAccount
}

/** 表單：認購 */
export interface IFormSubscription extends IFormBasic {
  /** 交易申請：認購 */
  trade_subscribe: {
    /** 
     * 認購交易日
     * @format YYYY-MM-DD
     */
    subscribe_date: string // YYYY-MM-DD
    /** 認購基金 */
    subscribe_fund: FundID
    /** 淨投資金額 */
    subscribe_amount: number
    /** 認購手續費 */
    subscribe_fee: number
    /** 認購手續費率 */
    subscribe_fee_rate: number
    /** Nominee 此筆認購是否含中國籍最終受益人 */
    is_beneficiary_in_china?: boolean | null
    /** Nominee 此筆認購中國籍最終受益人人數 */
    china_beneficiary_count?: number | null
  }
  /** 付款日期 */
  payment_date: null | string
}

/** 表單：贖回 */
export interface IFormRedeem extends IFormBasic {
  /** 交易申請：贖回 */
  trade_redeem: ITradeFormRedeem
}

/** 表單：轉換 */
export interface IFormSwitch extends IFormRedeem {
  /** 交易申請：轉換 */
  trade_switch: {
    /** 轉換月份 */
    switch_month: 'normal switch' | 'same month switch'
    /** 年限 */
    switch_carry_fwd: 'carry fwd' | 'no carry fwd'
    /** 認購基金 */
    subscribe_fund: FundID
    /** 認購手續費 */
    subscribe_fee: number
    /** 認購手續費率 */
    subscribe_fee_rate: number
  }
}

export function isFormSubscription(form: any): form is IFormMultiSubscribe {
  return !!(form as IFormMultiSubscribe)?.trade_subscribe
}

export function isFormRedeem(form: any): form is IFormMultiRedeem {
  return !!(form as IFormMultiRedeem)?.trade_redeem && !!(form as IFormMultiRedeem)?.trade_redeem_set
}

export function isFormSwitch(form: any): form is IFormMultiSwitch {
  return !!(form as IFormMultiSwitch)?.trade_switch && !!(form as IFormMultiSwitch)?.trade_switch_set
}

/** 水單版本的顯示資料；不可讀不代表檔案遺失。 */
export interface PaymentProofSnapshot {
  upload: { filename: string; url: string; path?: string | null } | null
  file_date: string | null
  version: string | null
  available: boolean
}

/** 相關檔案 */

export interface IFormFileUpload {
  key: FormFiles
  /** 載入水單時的正式版本；儲存時供後端拒絕過期覆寫，空水單為 missing。 */
  payment_proof_version?: string | null
  /** 首次編輯前的水單參照；舊快照可能無法取得。 */
  payment_proof_original?: PaymentProofSnapshot | null
  /** 使用者確認時看到的正式版本指紋；後端寫入前仍須重驗。 */
  payment_proof_confirmation?: string | null
  payment_proof_resolution?: 'replace' | 'keep'
  /** 待存水單與目前正式版本衝突，儲存時須逐筆確認。 */
  payment_proof_conflict?: boolean
  /** 衝突後正式版本的顯示資料；不等於使用者已確認取代。 */
  payment_proof_current_version?: string
  payment_proof_current_upload?: { filename: string, url: string }
  /** 主持有人的檔案，預設為 0 */
  holder_index: number
  /** 代表該持有人 (Holder) 名下的該類型 (Key) 檔案的第幾個檔案 */
  file_sequence: number
  /** 代表同單第幾筆交易的檔案，預設為 0 */
  trade_sequence: number
  /** 改單前實際保存此檔案的交易，供後端在序號重排時搬移檔案。 */
  source_trade_pno?: number
  file_date: string
  upload: null | {
    filename: string
    url: string
    mime?: string
    path?: string
  }
  /** 後端建立最終序號後要套用的正式檔名。 */
  target_filename?: string
  w8_signed_at?: null | string
}

export interface IFormFileProvide {
  source_trade_pno?: number
  key: FormFiles
  /** 載入水單時的正式版本；儲存時供後端拒絕過期覆寫，空水單為 missing。 */
  payment_proof_version?: string | null
  /** 首次編輯前的水單參照；舊快照可能無法取得。 */
  payment_proof_original?: PaymentProofSnapshot | null
  /** 使用者確認時看到的正式版本指紋；後端寫入前仍須重驗。 */
  payment_proof_confirmation?: string | null
  payment_proof_resolution?: 'replace' | 'keep'
  /** 待存水單與目前正式版本衝突，儲存時須逐筆確認。 */
  payment_proof_conflict?: boolean
  /** 衝突後正式版本的顯示資料；不等於使用者已確認取代。 */
  payment_proof_current_version?: string
  payment_proof_current_upload?: { filename: string, url: string }
  holder_index: number
  file_sequence: number
  trade_sequence: number
  file_date: string
  upload?: null | {
    filename: string
    url: string
    mime?: string
  }
  generated_file_url: null | string
  w8_signed_at?: null | string
}

export function isFileUpload(file: any): file is IFormFileUpload {
  return !!file?.upload
}

/** 後端可能保留空的 upload 佔位資料；只有可讀取的 URL 才算已有上傳檔案。 */
export function hasUploadedFile(file: any): file is IFormFileUpload {
  return typeof file?.upload?.url === 'string' && file.upload.url.length > 0
}

export function isFileProvide(file: any): file is IFormFileProvide {
  return !!file?.generated_file_url
}

export type FormFile = IFormFileUpload | IFormFileProvide


/** 建立 Holder */
export function createNewHolder(): Flexible<Holder> {
  return createNewShareHolder()
}

/** 建立 UG 聯名戶的自然人持有人，並鎖定帳戶資金來源。 */
export function createUgJointHolder(holder?: Flexible<IHolderCommon> | null): Flexible<Holder> {
  const ugHolder = deepAssign(createNewHolder(), holder ?? {})
  ugHolder.kyc.source_of_funds = ['investment_returns']
  return ugHolder
}

export const newPhone = () => ({
  country_code: null,
  area_code: null,
  phone_number: null,
})

export const newAddress = () => ({
  street: null,
  city: null,
  county: null,
  postal_code: null,
  country: null,
})

/** 建立 Shareholder */
export function createNewShareHolder(): Flexible<Shareholder> {
  return {
    profile: {
      personal_name: {
        first_name: null,
        last_name: null,
      },
      business_name: null,
      business_location: null,
      business_id_number: null,
      birthplace: null,
      date_of_birth: null,
      nationality: null,
      passport_issuing_place: null,
      passport_number: null,
      tax_payment_location: null,
      tax_id_number: null,
      no_tin_reason: null,
      no_tin_explain: null,
      second_nationality: null,
      second_tax_payment_location: null,
      second_tax_id_number: null,
      second_no_tin_reason: null,
      second_no_tin_explain: null,
      source_of_funds: null,
      shareholder_relationship: null,
      occupation: null,
    },
    contact: {
      phone_number: [newPhone()],
      fax_number: [newPhone()],
      email: [""],
      residence_address: newAddress(),
      company_address: newAddress(),
      mailing_address: newAddress(),
    },
    kyc: {
      source_of_funds: [],
      statement: {
        beneficiary: [],
      },
      source_proof: {},
      questionnaire: {},
      self_certification: {},
    },
  }
}
export function createNewForm(type: 'SBS', fillData?: Fillable<IFormMultiSubscribe>): IFormMultiSubscribe
export function createNewForm(type: 'RDM', fillData?: Fillable<IFormMultiRedeem>): IFormMultiRedeem
export function createNewForm(type: 'SWITCH', fillData?: Fillable<IFormMultiSwitch>): IFormMultiSwitch
export function createNewForm(type: 'SBS' | 'RDM' | 'SWITCH', fillData?: Fillable<DealingMultiForm>): DealingMultiForm

/** 建立空的表單 */
export function createNewForm(type: 'SBS' | 'RDM' | 'SWITCH', fillData?: Fillable<DealingMultiForm>): Flexible<IFormMultiSubscribe | IFormMultiRedeem | IFormMultiSwitch> {
  let form: Flexible<IFormMultiSubscribe | IFormMultiRedeem | IFormMultiSwitch>
  if (type === 'SBS') form = createNewSbsForm()
  else if (type === 'RDM') form = createNewRdmForm()
  else form = createNewSwitchForm()

  // 填入始值
  if (fillData) {
    deepAssign(form, fillData)
    if (form.account.account_type === AccountType.UGJoint) {
      form.account.holder = createUgJointHolder(form.account.holder ?? form.account.shareholders?.[0]) as Holder
      form.account.shareholders = []
    }
    if (form.account.shareholders) {
      form.account.shareholders = form.account.shareholders.map(sh =>
        //確保 shareholder 資料結構完整
        deepAssign(createNewShareHolder(), sh)
      )
    }
  }

  return form
}

export function canAddAccountShareholder(accountType: AccountType, shareholderCount: number): boolean {
  return accountType === AccountType.Joint && shareholderCount < 3
}

function createBankAccount(): Flexible<IHolderBankAccount> {
  return {
    bank_name: null,
    bank_address: null,
    bank_swift_code: null,
    beneficiary_bank_account_number: null,
    bank_account_name: null,
    bank_account_number: null,
    intermediary_bank_name: null,
    intermediary_bank_swift_code: null,
    second_intermediary_bank_account_name: null,
    second_intermediary_bank_account_number: null,
    bank_remark: null,
    bank_account_type: null,
    bank_joint_data_match: true,
    bank_account_change_update: null,
    bank_account_user_confirmed: false,
  }
}

function createNewCommonForm(): Flexible<IFormBasic> {
  return {
    account: {
      account_id: null,
      account_type: AccountType.Personal,
      holder: createNewHolder() as Holder,
      bank: createBankAccount(),
    },
    agent_id: null,
    sub_sales_id: null,
  }
}

export function createNewSbsForm(): Flexible<IFormMultiSubscribe> {
  const form: Flexible<IFormMultiSubscribe> = {
    ...createNewCommonForm(),
    trade_subscribe: {
      subscribe_date: null,
      subscribe_fund: null,
      subscribe_fee_rate: null,
    },
    trade_subscribe_set: [createSubscribeSet()]
  }
  form.account.bank.bank_account_change_update = true
  return form
}

export function createNewRdmForm(): Flexible<IFormMultiRedeem> {
  const form: Flexible<IFormMultiRedeem> = {
    ...createNewCommonForm(),
    trade_redeem: {
      redeem_date: null,
      redeem_fund: null,
    },
    trade_redeem_set: [createRedeemSet()]
  }
  return form
}

export function createNewSwitchForm(): Flexible<IFormMultiSwitch> {
  const form: Flexible<IFormMultiSwitch> = {
    ...createNewCommonForm(),
    trade_switch: {
      switch_month: 'normal switch',
      switch_carry_fwd: 'no carry fwd',
      switch_from: null,
      switch_to: null,
      redeem_date: null,
      subscribe_fee_rate: null,
    },
    trade_switch_set: [createSwitchSet()]
  }
  form.account.bank.bank_account_change_update = true
  return form
}

/** 建立一組淨投資金額  */
export function createSubscribeSet():ISubscribeSet {
  return {
    subscribe_amount: 0,
    subscribe_fee: null,
    is_beneficiary_in_china: null,
    china_beneficiary_count: null,
  }
}

/** 依中國籍人數同步單筆交易的中國籍欄位；空值保留未填、0 代表非中國籍。 */
export function updateChinaBeneficiaryCount(
  set: Pick<ISubscribeSet, 'is_beneficiary_in_china' | 'china_beneficiary_count'>,
  value: unknown,
): void {
  if (value === '' || value === null || value === undefined) {
    set.china_beneficiary_count = null
    set.is_beneficiary_in_china = null
    return
  }

  const count = Number(value)
  set.china_beneficiary_count = Number.isInteger(count) && count >= 0 ? count : null
  set.is_beneficiary_in_china = set.china_beneficiary_count === null
    ? null
    : set.china_beneficiary_count > 0
}

/** 建立一組贖回股份數目 */
export function createRedeemSet():IRedeemSet {
  return {
    redeem_method: "Shares",
    redeem_amount: null,
    redeem_shares: null,
    redeem_fee_rate: null,
    redeem_from: [],
  }
}

/** 依所選庫存建立指定贖回 set；中國籍欄位依現行 contract 放在 redeem_from 外層。 */
export function createInventoryRedeemSet(inventory: IFormRedeemInventory): IRedeemSet {
  const { is_beneficiary_in_china = null, ...redeemFrom } = inventory
  delete redeemFrom.china_beneficiary_count

  return {
    ...createRedeemSet(),
    redeem_method: 'Inventory',
    redeem_from: [redeemFrom],
    is_beneficiary_in_china,
    china_beneficiary_count: null,
  }
}

/** 更換既有列的指定部位，保留交易識別、股數及費用；RDM／Switch 皆以外層標示識別庫存。 */
export function replaceRedeemInventory(set: IRedeemSet | ISwitchSet, inventory: IFormRedeemInventory): void {
  const shares = set.redeem_from[0]?.shares ?? 0
  const selected = { ...inventory, shares }
  set.redeem_from = createInventoryRedeemSet(selected).redeem_from
  set.is_beneficiary_in_china = inventory.is_beneficiary_in_china ?? null
  set.china_beneficiary_count = null
}

/** 指定庫存的人數輸入不改變已選部位的中國籍身分；非中國籍部位不填人數。 */
export function updateInventoryChinaBeneficiaryCount(set: IRedeemSet | ISwitchSet, value: unknown): void {
  const count = value === '' || value == null ? null : Number(value)
  set.china_beneficiary_count = set.is_beneficiary_in_china === true && count !== null && Number.isInteger(count) && count >= 0 ? count : null
}

/** 建立一組轉換股份數目  */
export function createSwitchSet():ISwitchSet {
  return {
    redeem_method: 'Shares',
    redeem_amount: null,
    redeem_shares: null,
    redeem_fee_rate: null,
    redeem_from: [],

    subscribe_fee: null,
  }
}

/** 顯示 Holder 名稱；UG 聯名戶等無主 holder 的帳戶回傳空字串。 */
export function getHolderName(_holder: Flexible<IHolderCommon> | null | undefined): string {
  const holder = toValue(_holder)
  if (!holder) return ''
  return (holder.profile && holder.profile.business_name) || ((holder.profile.personal_name !== undefined) && [
    holder.profile.personal_name.last_name,
    holder.profile.personal_name.first_name,
  ].filter((exists): exists is string => !!exists).join(', ')) || ''
}

interface IAccountLike {
  account_type?: AccountType | null
  holder?: Flexible<IHolderCommon> | null
  shareholders?: Flexible<IHolderCommon>[] | null
}

/** 顯示帳戶名稱 */
export function getAccountName(account: IAccountLike): string {
  const { account_type, holder, shareholders } = account
  const holderNames = [
    holder,
    ...shareholders || []
  ].filter(Boolean)
    .map(holder => getHolderName(holder as Flexible<IHolderCommon>))
    .filter((exists): exists is string => !!exists)

  /** UG Joint 顯示固定 UG 公司主體及自然人持有人。 */
  if (account_type === AccountType.UGJoint) {
    holderNames.unshift(UG_JOINT_HOLDER_NAME)
  }

  return holderNames.join(' & ')
}
